'use client'

import { useSearchParams } from 'next/navigation'
 import React, { useEffect, useMemo, useRef, useState } from 'react'

 import { convertParsedToFrameData, parseCIF, parsePDB } from '@/components/py2Dmol-ts-react/lib/parsing'
import { Skeleton } from '@/components/ui/skeleton'
 import { initializePy2DmolViewer } from '@/components/py2Dmol-ts-react/renderer/runtime'
 
 type Py2dMolMiniPreviewProps = {
   /** URL that returns mmCIF/PDB text (we use /api/structure?...). */
   url: string
   format: 'cif' | 'pdb'
   /** Unique-ish id for viewer registration; ok to reuse across renders for same card */
   viewerId: string
 }
 
 export default function Py2dMolMiniPreview({ url, format, viewerId }: Py2dMolMiniPreviewProps) {
  const searchParams = useSearchParams()
   const rootRef = useRef<HTMLDivElement | null>(null)
   const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
 
   const objectName = useMemo(() => `obj-${viewerId}`, [viewerId])
  const scopedUrl = useMemo(() => {
    try {
      const teamId = String(searchParams.get('team_id') || '').trim()
      if (!teamId) return url
      const parsed = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost')
      if (!parsed.searchParams.get('team_id')) {
        parsed.searchParams.set('team_id', teamId)
      }
      if (/^https?:\/\//i.test(url)) return parsed.toString()
      return `${parsed.pathname}${parsed.search}${parsed.hash}`
    } catch {
      return url
    }
  }, [url, searchParams])
 
   useEffect(() => {
     let cancelled = false
 
     const run = async () => {
       try {
         setError(null)
        setIsLoading(true)
         const root = rootRef.current
         if (!root) return
 
         // Configure runtime for a lightweight thumbnail
         ;(window as any).viewerConfig = {
           size: [300, 300],
           pae_size: [300, 300],
           color: 'auto',
           shadow: false,
           outline: false,
           width: 3.0,
           rotate: false,
           controls: false,
           autoplay: false,
           box: false,
           pastel: 0.15,
           pae: false,
           colorblind: false,
           viewer_id: viewerId,
           biounit: false,
           ignoreLigands: true,
           previewMode: true,
           interactive: false,
         }
 
         // Mount (scoped queries use ids within this root)
         initializePy2DmolViewer(root)
         const api = (window as any).py2dmol_viewers?.[viewerId]
         if (!api) throw new Error('Failed to initialize preview')
 
        const res = await fetch(scopedUrl, { credentials: 'include' })
         if (!res.ok) throw new Error(`Failed to fetch structure (HTTP ${res.status})`)
         const text = await res.text()
         if (cancelled) return
         if (!text || text.trim().length === 0) throw new Error('Empty structure')
 
         // Parse first model only (thumbnail)
         const isCif = format === 'cif' || /^\s*data_/m.test(text) || /_atom_site\./.test(text)
         const models = isCif ? parseCIF(text) : parsePDB(text)
         const model0 = Array.isArray(models) && models.length > 0 ? models[0] : []
         if (!model0 || model0.length === 0) throw new Error('No atoms found')
 
         const frame = convertParsedToFrameData(model0)
         api.handlePythonClearAll()
         api.handlePythonNewObject(objectName)
         api.handlePythonUpdate(JSON.stringify(frame), objectName)
       } catch (e) {
         if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to render preview')
      } finally {
        if (!cancelled) setIsLoading(false)
       }
     }
 
     void run()
     return () => {
       cancelled = true
       try {
         // Best effort: remove api ref to avoid leaking memory
         if ((window as any).py2dmol_viewers && (window as any).py2dmol_viewers[viewerId]) {
           delete (window as any).py2dmol_viewers[viewerId]
         }
       } catch { }
     }
  }, [format, objectName, scopedUrl, viewerId])
 
   return (
     <div ref={rootRef} className="w-full h-full relative" data-viewer-root={viewerId}>
       {/* Minimal DOM expected by the renderer runtime (scoped to this root) */}
       <div id="canvasContainer" className="absolute inset-0">
         <canvas id="canvas" className="w-full h-full" />
       </div>
 
      {isLoading && !error && (
        <div className="absolute inset-0 bg-background/40 p-3 pointer-events-none">
          <Skeleton className="h-full w-full rounded-lg" />
        </div>
      )}

       {/* Required-but-hidden controls DOM (runtime queries by id) */}
       <div id="controlsContainer" className="hidden">
         <button id="playButton" />
         <input id="frameSlider" type="range" />
         <span id="frameCounter" />
         <select id="objectSelect" />
         <select id="speedSelect" />
         <input id="rotationCheckbox" type="checkbox" />
         <input id="lineWidthSlider" type="range" />
         <select id="colorSelect" />
         <input id="shadowEnabledCheckbox" type="checkbox" />
         <input id="outlineEnabledCheckbox" type="checkbox" />
         <input id="colorblindCheckbox" type="checkbox" />
       </div>
       <div id="paeContainer" className="hidden">
         <canvas id="paeCanvas" />
       </div>
       <div id="rightPanelContainer" className="hidden" />
       <div id="objectContainer" className="hidden" />
 
       {error && (
         <div className="absolute inset-0 flex items-center justify-center text-[11px] text-muted-foreground bg-background/60">
           {error}
         </div>
       )}
     </div>
   )
 }

